import { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { useAppSelector } from "../../app";
import { useActiveDocumentSet } from "../../documentSet";
import useActiveDocument from "../../document/hooks/useActiveDocument";
import useMeasure from "react-use-measure";

const useAnnotationSideControls = (documentSetId?: string) => {
  const [isLoading, setIsLoading] = useState(false);
  const [alertHeight, setAlertHeight] = useState(0);
  const [duplicateAlertHeight, setDuplicateAlertHeight] = useState(0);
  const [heightFooter, setHeightFooter] = useState(0);

  const [currentHeight, setCurrentHeight] = useState(0);

  const annotationSideControlsClassnames = classNames({
    "transition-view": true,
    "in-detail-view": true,
  });

  const userSettings = useAppSelector(
    (state) => state.userReducer.userSettings,
  );

  const { isTableControlsResizing } = useAppSelector(
    (state) => state.annotationTableControlsReducer,
  );

  const { isTokenRefreshing } = useAppSelector((state) => state.userReducer);

  const { fetchDocumentActiveDocumentSet, activeDocumentSet } =
    useActiveDocumentSet();

  const { fetchDocument, setActiveDocument } = useActiveDocument();

  const [containerRef, { height: heightContainer }] = useMeasure();

  const fetchDocumentSetRef = useRef(fetchDocumentActiveDocumentSet);

  useEffect(() => {
    fetchDocumentSetRef.current = fetchDocumentActiveDocumentSet;
  }, [fetchDocumentActiveDocumentSet]);

  useEffect(() => {
    const controller = new AbortController();

    if (documentSetId && !isTokenRefreshing) {
      fetchDocumentSetRef.current(documentSetId, controller.signal, true);
    }

    return () => controller.abort();
  }, [documentSetId, isTokenRefreshing]);

  useEffect(() => {
    const controller = new AbortController();
    if (!activeDocumentSet?.documents?.length) {
      setActiveDocument();
      return;
    }

    fetchDocument(
      activeDocumentSet.documents[0].id,
      activeDocumentSet.status,
      controller.signal,
    );

    return () => controller.abort();
    // The useEffect triggers to many times due to fetchDocument, so we exclude it from the dependency array
    // eslint-disable-next-line
  }, [activeDocumentSet?.documents, setActiveDocument]);

  // Avoid setting the actual height during table control resizing to prevent excessive rendering.
  // Mirroring the measured height while rendering rather than from an effect
  // keeps the exact same guard and settles on the same value, one paint earlier.
  // (`tableControlsHeight` was only an extra trigger in the old dependency
  // array; re-running for it could never do anything but set the same value.)
  if (!isTableControlsResizing && currentHeight !== heightContainer) {
    setCurrentHeight(heightContainer);
  }

  return {
    isLoading,
    setIsLoading,
    alertHeight,
    duplicateAlertHeight,
    heightFooter,
    setHeightFooter,
    annotationSideControlsClassnames,
    userSettings,
    activeDocumentSet,
    setAlertHeight,
    setDuplicateAlertHeight,
    currentHeight,
    containerRef,
  };
};

export default useAnnotationSideControls;
